import { DEFAULT_TEMPLATE_ID } from '@cibli/shared';

type LocaleCode =
  'fr-FR' | 'en-GB' | 'de-DE' | 'es-ES' | 'pt-PT' | 'uk-UA' | 'ar-DZ' | 'ar-MA' | 'tr-TR' | 'ro-RO';

interface CvExempleData {
  personalInfo: {
    postalCode: string;
    location: string;
    jobTitle: string;
    hasDriverLicense: boolean;
    driverLicenseTypes: string;
    openToTraining: boolean;
    gender: null;
    hobbiesAndInterests: string[];
    mobility: { isMobile: boolean; range: string; radius: number };
  };
  experiences: {
    professional: Array<{
      company: string;
      location: string;
      missions: string;
      title: string;
      startDate: string;
      endDate: string | null;
    }>;
    personal: Array<{
      title: string;
      description: string;
      location: string | null;
      startDate: string;
      endDate: string;
    }>;
  };
  skills: {
    skills: string[];
    strengths: string[];
    preferedTasks: string[];
    notPreferedTasks: string[];
    languages: Array<{ language: string; level: string }>;
  };
  education: {
    schools: Array<{
      school: string;
      name: string;
      degree: string;
      location: string | null;
      acquiredSkills: string[];
      degreeStartedAt: string;
      degreeObtainedAt: string | null;
    }>;
    certifications: Array<{
      certification: string | null;
      issuingOrganization: string | null;
      certificationObtainedAt: string | null;
      acquiredSkills: string[] | null;
    }>;
  };
  isCompleted: boolean;
  summary: string;
  question: null;
  isReconversion: boolean;
  complementaryInfo: {
    hasRQTH: boolean;
    mentions: string[];
  };
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  hasDriverLicense: boolean;
  isMobile: boolean;
  openToTraining: boolean;
  gender: null;
  hobbiesAndInterests: string[];
  location: string;
  mobilityRadius: number;
  mobilityRange: string;
  postalCode: string;
  pictureBase64: string | null;
  jobTitle: string;
  personalExperiences: Array<{
    title: string;
    description: string;
    location: string | null;
    startDate: string;
    endDate: string;
  }>;
  competences: string[];
  languages: Array<{ language: string; level: string }>;
  interviewId: string;
  templateParams: Record<string, unknown>;
  template: string;
  locale: { name: string; codeShort: string; codeLong: string };
}

const CV_DATA: Record<LocaleCode, CvExempleData> = {
  'fr-FR': {
    personalInfo: {
      postalCode: '75001',
      location: 'Paris',
      jobTitle: 'Développeur Full Stack',
      hasDriverLicense: true,
      driverLicenseTypes: 'B',
      openToTraining: true,
      gender: null,
      hobbiesAndInterests: ['Lecture', 'Voyages', 'Photographie'],
      mobility: { isMobile: true, range: '200km', radius: 200 },
    },
    experiences: {
      professional: [
        {
          company: 'TechSolutions',
          location: 'Paris, France',
          title: 'Développeur Full Stack',
          missions:
            "Conception et développement d'applications web robustes avec React, Node.js et PostgreSQL.\nParticipation aux revues de code et amélioration continue des processus de développement.\nMentorat des développeurs juniors et animation de sessions de partage de connaissances.",
          startDate: '2022-03-01',
          endDate: null,
        },
        {
          company: 'Digital Agency',
          location: 'Lyon, France',
          title: 'Développeur Frontend',
          missions:
            "Création d'interfaces utilisateur interactives et responsives avec React et TypeScript.\nCollaboration étroite avec les designers pour implémenter les maquettes Figma.\nOptimisation des performances web et mise en place de tests E2E avec Cypress.",
          startDate: '2020-06-01',
          endDate: '2022-02-28',
        },
        {
          company: 'StartupLab',
          location: 'Nantes, France',
          title: 'Développeur Junior',
          missions:
            "Développement de fonctionnalités front-end pour une application SaaS de gestion de projets.\nCorrection de bugs et maintenance d'un legacy PHP vers une migration React.\nParticipation aux sprints agiles et rétrospectives d'équipe.",
          startDate: '2018-09-01',
          endDate: '2020-05-31',
        },
        {
          company: 'WebForce3',
          location: 'Bordeaux, France',
          title: 'Stagiaire Développeur',
          missions:
            "Intégration de maquettes HTML/CSS pour des sites e-commerce.\nDéveloppement de composants réutilisables avec Vue.js.\nParticipation à la mise en place d'un CMS sur mesure.",
          startDate: '2018-03-01',
          endDate: '2018-08-31',
        },
        {
          company: 'Freelance',
          location: 'Paris, France',
          title: 'Développeur Web Freelance',
          missions:
            "Création de sites vitrines et d'applications web pour des clients PME.\nGestion complète du cycle de projet : cahier des charges, développement, déploiement.\nMaintenance et évolution de sites existants.",
          startDate: '2017-06-01',
          endDate: '2018-02-28',
        },
      ],
      personal: [
        {
          title: 'Blog technique',
          description:
            "Rédaction d'articles techniques sur le développement web moderne.\nCréation de tutoriels vidéo sur YouTube autour de React et TypeScript.",
          location: null,
          startDate: '2023-01-01',
          endDate: '2023-12-31',
        },
        {
          title: 'Application mobile bénévolat',
          description:
            "Conception et développement d'une application mobile React Native pour une association caritative.\nMise en place d'un système de notification push et synchronisation cloud.",
          location: null,
          startDate: '2022-06-01',
          endDate: '2022-12-31',
        },
        {
          title: 'Projet Open Source',
          description:
            "Participation au développement d'une bibliothèque JavaScript open source.\nGestion des issues et revue des pull requests sur GitHub.\nRédaction de la documentation et création de tutoriels pour les nouveaux contributeurs.",
          location: null,
          startDate: '2021-06-01',
          endDate: '2021-12-31',
        },
        {
          title: 'Contributeur WordPress',
          description:
            'Développement de plugins WordPress personnalisés pour la communauté open source.\nRéponse aux questions sur les forums et participation aux meetups locaux.',
          location: null,
          startDate: '2019-03-01',
          endDate: '2019-12-31',
        },
      ],
    },
    skills: {
      skills: ['JavaScript', 'TypeScript', 'React', 'Node.js', 'PostgreSQL'],
      strengths: [],
      preferedTasks: [],
      notPreferedTasks: [],
      languages: [
        { language: 'Français', level: 'Langue maternelle' },
        { language: 'Anglais', level: 'Courant' },
        { language: 'Espagnol', level: 'Intermédiaire' },
      ],
    },
    education: {
      schools: [
        {
          school: 'Epitech',
          name: 'Informatique',
          degree: "Bac +5 - Expert en technologies de l'information",
          acquiredSkills: ['JavaScript', 'TypeScript', 'React', 'Node.js'],
          location: null,
          degreeStartedAt: '2018-09-01',
          degreeObtainedAt: '2023-06-30',
        },
        {
          school: 'Université de Lyon',
          name: 'Mathématiques et Informatique',
          degree: 'Licence - Mathématiques appliquées',
          acquiredSkills: ['Algorithmique', 'Python', 'Bases de données'],
          location: null,
          degreeStartedAt: '2015-09-01',
          degreeObtainedAt: '2018-06-30',
        },
        {
          school: 'OpenClassrooms',
          name: 'Formation Continue',
          degree: 'Certification Développeur Web',
          acquiredSkills: ['HTML', 'CSS', 'JavaScript', 'React'],
          location: null,
          degreeStartedAt: '2019-01-01',
          degreeObtainedAt: '2019-06-30',
        },
      ],
      certifications: [],
    },
    isCompleted: true,
    summary:
      "Développeur Full Stack passionné avec plus de 4 ans d'expérience dans la création d'applications web modernes et performantes. Je maîtrise l'ensemble du stack technique, du frontend avec React et TypeScript au backend avec Node.js et PostgreSQL.",
    question: null,
    isReconversion: false,
    complementaryInfo: {
      hasRQTH: true,
      mentions: ['Prêt à déménager', 'Besoin de la chaise ergonomique'],
    },
    firstName: 'Marie',
    lastName: 'Dupont',
    email: '[EMAIL]',
    phone: '[PHONE]',
    hasDriverLicense: true,
    isMobile: true,
    openToTraining: true,
    gender: null,
    hobbiesAndInterests: ['Lecture', 'Voyages', 'Photographie'],
    location: 'Paris',
    mobilityRadius: 200,
    mobilityRange: '200km',
    postalCode: '75001',
    pictureBase64: '/red-panda.jpeg',
    jobTitle: 'Développeur Full Stack',
    personalExperiences: [
      {
        title: 'Blog technique',
        description: "Rédaction d'articles techniques sur le développement web moderne.",
        location: null,
        startDate: '2023-01-01',
        endDate: '2023-12-31',
      },
      {
        title: 'Application mobile bénévolat',
        description:
          "Conception et développement d'une application mobile React Native pour une association caritative.",
        location: null,
        startDate: '2022-06-01',
        endDate: '2022-12-31',
      },
      {
        title: 'Projet Open Source',
        description: "Participation au développement d'une bibliothèque JavaScript open source.",
        location: null,
        startDate: '2021-06-01',
        endDate: '2021-12-31',
      },
      {
        title: 'Contributeur WordPress',
        description:
          'Développement de plugins WordPress personnalisés pour la communauté open source.',
        location: null,
        startDate: '2019-03-01',
        endDate: '2019-12-31',
      },
    ],
    competences: ['JavaScript', 'TypeScript', 'React', 'Node.js', 'PostgreSQL'],
    languages: [
      { language: 'Français', level: 'Langue maternelle' },
      { language: 'Anglais', level: 'Courant' },
      { language: 'Espagnol', level: 'Intermédiaire' },
    ],
    interviewId: 'example-interview-id',
    templateParams: {},
    template: DEFAULT_TEMPLATE_ID,
    locale: { name: 'Français', codeShort: 'fr', codeLong: 'fr-FR' },
  },

  'en-GB': {
    personalInfo: {
      postalCode: '75001',
      location: 'Paris',
      jobTitle: 'Full Stack Developer',
      hasDriverLicense: true,
      driverLicenseTypes: 'B',
      openToTraining: true,
      gender: null,
      hobbiesAndInterests: ['Reading', 'Travel', 'Photography', 'Hiking'],
      mobility: { isMobile: true, range: '200km', radius: 200 },
    },
    experiences: {
      professional: [
        {
          company: 'TechSolutions',
          location: 'Paris, France',
          title: 'Full Stack Developer',
          missions:
            'Designing and developing robust web applications with React, Node.js, and PostgreSQL.\nParticipating in code reviews and continuous improvement of development processes.\nMentoring junior developers and leading knowledge-sharing sessions.',
          startDate: '2022-03-01',
          endDate: null,
        },
        {
          company: 'Digital Agency',
          location: 'Lyon, France',
          title: 'Frontend Developer',
          missions:
            'Creating interactive and responsive user interfaces with React and TypeScript.\nCollaborating closely with designers to implement Figma mockups.\nOptimizing web performance and implementing E2E tests with Cypress.',
          startDate: '2020-06-01',
          endDate: '2022-02-28',
        },
      ],
      personal: [
        {
          title: 'Open Source Project',
          description:
            'Contributing to the development of an open source JavaScript library.\nManaging issues and reviewing pull requests on GitHub.\nWriting documentation and creating tutorials for new contributors.',
          location: null,
          startDate: '2021-06-01',
          endDate: '2021-12-31',
        },
      ],
    },
    skills: {
      skills: [
        'JavaScript',
        'TypeScript',
        'React',
        'Node.js',
        'Express',
        'MongoDB',
        'PostgreSQL',
        'Docker',
        'Git',
      ],
      strengths: [],
      preferedTasks: [],
      notPreferedTasks: [],
      languages: [
        { language: 'French', level: 'Native' },
        { language: 'English', level: 'Fluent' },
      ],
    },
    education: {
      schools: [
        {
          school: 'Epitech',
          name: 'Computer Science',
          degree: 'Masters - Expert in Information Technology',
          acquiredSkills: ['JavaScript', 'TypeScript', 'React', 'Node.js'],
          location: null,
          degreeStartedAt: '2024-09-01',
          degreeObtainedAt: null,
        },
      ],
      certifications: [],
    },
    isCompleted: true,
    summary:
      'Passionate Full Stack Developer with over 4 years of experience creating modern, high-performance web applications. Proficient in the full tech stack, from frontend with React and TypeScript to backend with Node.js and PostgreSQL.',
    question: null,
    isReconversion: false,
    complementaryInfo: { hasRQTH: false, mentions: [] },
    firstName: 'Marie',
    lastName: 'Dupont',
    email: '[EMAIL]',
    phone: '[PHONE]',
    hasDriverLicense: true,
    isMobile: true,
    openToTraining: true,
    gender: null,
    hobbiesAndInterests: ['Reading', 'Travel', 'Photography', 'Hiking'],
    location: 'Paris',
    mobilityRadius: 200,
    mobilityRange: '200km',
    postalCode: '75001',
    pictureBase64: null,
    jobTitle: 'Full Stack Developer',
    personalExperiences: [
      {
        title: 'Open Source Project',
        description: 'Contributing to the development of an open source JavaScript library.',
        location: null,
        startDate: '2021-06-01',
        endDate: '2021-12-31',
      },
    ],
    competences: [
      'JavaScript',
      'TypeScript',
      'React',
      'Node.js',
      'Express',
      'MongoDB',
      'PostgreSQL',
      'Docker',
      'Git',
    ],
    languages: [
      { language: 'French', level: 'Native' },
      { language: 'English', level: 'Fluent' },
    ],
    interviewId: 'example-interview-id',
    templateParams: {},
    template: DEFAULT_TEMPLATE_ID,
    locale: { name: 'English', codeShort: 'en', codeLong: 'en-GB' },
  },

  'de-DE': {
    personalInfo: {
      postalCode: '10115',
      location: 'Berlin',
      jobTitle: 'Vollstack-Entwickler',
      hasDriverLicense: true,
      driverLicenseTypes: 'B',
      openToTraining: true,
      gender: null,
      hobbiesAndInterests: ['Lesen', 'Reisen', 'Fotografie', 'Wandern'],
      mobility: { isMobile: true, range: '200km', radius: 200 },
    },
    experiences: {
      professional: [
        {
          company: 'TechSolutions',
          location: 'Berlin, Deutschland',
          title: 'Vollstack-Entwickler',
          missions:
            'Konzeption und Entwicklung robuster Webanwendungen mit React, Node.js und PostgreSQL.\nTeilnahme an Code-Reviews und kontinuierliche Verbesserung der Entwicklungsprozesse.\nMentoring von Junior-Entwicklern und Leitung von Wissensaustausch-Sitzungen.',
          startDate: '2022-03-01',
          endDate: null,
        },
        {
          company: 'Digital Agency',
          location: 'München, Deutschland',
          title: 'Frontend-Entwickler',
          missions:
            'Erstellung interaktiver und responsiver Benutzeroberflächen mit React und TypeScript.\nEnge Zusammenarbeit mit Designern bei der Implementierung von Figma-Mockups.\nOptimierung der Web-Performance und Implementierung von E2E-Tests mit Cypress.',
          startDate: '2020-06-01',
          endDate: '2022-02-28',
        },
      ],
      personal: [
        {
          title: 'Open-Source-Projekt',
          description:
            'Mitwirkung an der Entwicklung einer Open-Source-JavaScript-Bibliothek.\nVerwaltung von Issues und Überprüfung von Pull-Requests auf GitHub.\nErstellung von Dokumentation und Tutorials für neue Mitwirkende.',
          location: null,
          startDate: '2021-06-01',
          endDate: '2021-12-31',
        },
      ],
    },
    skills: {
      skills: [
        'JavaScript',
        'TypeScript',
        'React',
        'Node.js',
        'Express',
        'MongoDB',
        'PostgreSQL',
        'Docker',
        'Git',
      ],
      strengths: [],
      preferedTasks: [],
      notPreferedTasks: [],
      languages: [
        { language: 'Deutsch', level: 'Muttersprache' },
        { language: 'Englisch', level: 'Fließend' },
      ],
    },
    education: {
      schools: [
        {
          school: 'TU Berlin',
          name: 'Informatik',
          degree: 'Master - Experte für Informationstechnologie',
          acquiredSkills: ['JavaScript', 'TypeScript', 'React', 'Node.js'],
          location: null,
          degreeStartedAt: '2024-09-01',
          degreeObtainedAt: null,
        },
      ],
      certifications: [],
    },
    isCompleted: true,
    summary:
      'Leidenschaftlicher Vollstack-Entwickler mit über 4 Jahren Erfahrung in der Erstellung moderner, leistungsstarker Webanwendungen. Beherrsche den gesamten Technologie-Stack, vom Frontend mit React und TypeScript bis zum Backend mit Node.js und PostgreSQL.',
    question: null,
    isReconversion: false,
    complementaryInfo: { hasRQTH: false, mentions: [] },
    firstName: 'Marie',
    lastName: 'Dupont',
    email: '[EMAIL]',
    phone: '[PHONE]',
    hasDriverLicense: true,
    isMobile: true,
    openToTraining: true,
    gender: null,
    hobbiesAndInterests: ['Lesen', 'Reisen', 'Fotografie', 'Wandern'],
    location: 'Berlin',
    mobilityRadius: 200,
    mobilityRange: '200km',
    postalCode: '10115',
    pictureBase64: null,
    jobTitle: 'Vollstack-Entwickler',
    personalExperiences: [
      {
        title: 'Open-Source-Projekt',
        description: 'Mitwirkung an der Entwicklung einer Open-Source-JavaScript-Bibliothek.',
        location: null,
        startDate: '2021-06-01',
        endDate: '2021-12-31',
      },
    ],
    competences: [
      'JavaScript',
      'TypeScript',
      'React',
      'Node.js',
      'Express',
      'MongoDB',
      'PostgreSQL',
      'Docker',
      'Git',
    ],
    languages: [
      { language: 'Deutsch', level: 'Muttersprache' },
      { language: 'Englisch', level: 'Fließend' },
    ],
    interviewId: 'example-interview-id',
    templateParams: {},
    template: DEFAULT_TEMPLATE_ID,
    locale: { name: 'Deutsch', codeShort: 'de', codeLong: 'de-DE' },
  },

  'es-ES': {
    personalInfo: {
      postalCode: '28001',
      location: 'Madrid',
      jobTitle: 'Desarrollador Full Stack',
      hasDriverLicense: true,
      driverLicenseTypes: 'B',
      openToTraining: true,
      gender: null,
      hobbiesAndInterests: ['Lectura', 'Viajes', 'Fotografía', 'Senderismo'],
      mobility: { isMobile: true, range: '200km', radius: 200 },
    },
    experiences: {
      professional: [
        {
          company: 'TechSolutions',
          location: 'Madrid, España',
          title: 'Desarrollador Full Stack',
          missions:
            'Concepción y desarrollo de aplicaciones web robustas con React, Node.js y PostgreSQL.\nParticipación en revisiones de código y mejora continua de los procesos de desarrollo.\nMentoría de desarrolladores junior y dirección de sesiones de intercambio de conocimientos.',
          startDate: '2022-03-01',
          endDate: null,
        },
        {
          company: 'Digital Agency',
          location: 'Barcelona, España',
          title: 'Desarrollador Frontend',
          missions:
            'Creación de interfaces de usuario interactivas y responsivas con React y TypeScript.\nColaboración estrecha con diseñadores para implementar mockups de Figma.\nOptimización del rendimiento web e implementación de pruebas E2E con Cypress.',
          startDate: '2020-06-01',
          endDate: '2022-02-28',
        },
      ],
      personal: [
        {
          title: 'Proyecto Open Source',
          description:
            'Contribución al desarrollo de una biblioteca JavaScript de código abierto.\nGestión de issues y revisión de pull requests en GitHub.\nRedacción de documentación y creación de tutoriales para nuevos colaboradores.',
          location: null,
          startDate: '2021-06-01',
          endDate: '2021-12-31',
        },
      ],
    },
    skills: {
      skills: [
        'JavaScript',
        'TypeScript',
        'React',
        'Node.js',
        'Express',
        'MongoDB',
        'PostgreSQL',
        'Docker',
        'Git',
      ],
      strengths: [],
      preferedTasks: [],
      notPreferedTasks: [],
      languages: [
        { language: 'Español', level: 'Nativo' },
        { language: 'Inglés', level: 'Fluido' },
      ],
    },
    education: {
      schools: [
        {
          school: 'UPM',
          name: 'Informática',
          degree: 'Máster - Experto en tecnologías de la información',
          acquiredSkills: ['JavaScript', 'TypeScript', 'React', 'Node.js'],
          location: null,
          degreeStartedAt: '2024-09-01',
          degreeObtainedAt: null,
        },
      ],
      certifications: [],
    },
    isCompleted: true,
    summary:
      'Desarrollador Full Stack apasionado con más de 4 años de experiencia en la creación de aplicaciones web modernas y de alto rendimiento. Dominio completo del stack técnico, desde el frontend con React y TypeScript hasta el backend con Node.js y PostgreSQL.',
    question: null,
    isReconversion: false,
    complementaryInfo: { hasRQTH: false, mentions: [] },
    firstName: 'Marie',
    lastName: 'Dupont',
    email: '[EMAIL]',
    phone: '[PHONE]',
    hasDriverLicense: true,
    isMobile: true,
    openToTraining: true,
    gender: null,
    hobbiesAndInterests: ['Lectura', 'Viajes', 'Fotografía', 'Senderismo'],
    location: 'Madrid',
    mobilityRadius: 200,
    mobilityRange: '200km',
    postalCode: '28001',
    pictureBase64: null,
    jobTitle: 'Desarrollador Full Stack',
    personalExperiences: [
      {
        title: 'Proyecto Open Source',
        description: 'Contribución al desarrollo de una biblioteca JavaScript de código abierto.',
        location: null,
        startDate: '2021-06-01',
        endDate: '2021-12-31',
      },
    ],
    competences: [
      'JavaScript',
      'TypeScript',
      'React',
      'Node.js',
      'Express',
      'MongoDB',
      'PostgreSQL',
      'Docker',
      'Git',
    ],
    languages: [
      { language: 'Español', level: 'Nativo' },
      { language: 'Inglés', level: 'Fluido' },
    ],
    interviewId: 'example-interview-id',
    templateParams: {},
    template: DEFAULT_TEMPLATE_ID,
    locale: { name: 'Español', codeShort: 'es', codeLong: 'es-ES' },
  },

  'pt-PT': {
    personalInfo: {
      postalCode: '1000-001',
      location: 'Lisboa',
      jobTitle: 'Desenvolvedor Full Stack',
      hasDriverLicense: true,
      driverLicenseTypes: 'B',
      openToTraining: true,
      gender: null,
      hobbiesAndInterests: ['Leitura', 'Viagens', 'Fotografia', 'Caminhadas'],
      mobility: { isMobile: true, range: '200km', radius: 200 },
    },
    experiences: {
      professional: [
        {
          company: 'TechSolutions',
          location: 'Lisboa, Portugal',
          title: 'Desenvolvedor Full Stack',
          missions:
            'Conceção e desenvolvimento de aplicações web robustas com React, Node.js e PostgreSQL.\nParticipação em revisões de código e melhoria contínua dos processos de desenvolvimento.\nMentoria de desenvolvedores juniores e liderança de sessões de partilha de conhecimentos.',
          startDate: '2022-03-01',
          endDate: null,
        },
        {
          company: 'Digital Agency',
          location: 'Porto, Portugal',
          title: 'Desenvolvedor Frontend',
          missions:
            'Criação de interfaces de utilizador interativas e responsivas com React e TypeScript.\nColaboração estreita com designers para implementar mockups Figma.\nOtimização do desempenho web e implementação de testes E2E com Cypress.',
          startDate: '2020-06-01',
          endDate: '2022-02-28',
        },
      ],
      personal: [
        {
          title: 'Projeto Open Source',
          description:
            'Contribuição para o desenvolvimento de uma biblioteca JavaScript open source.\nGestão de issues e revisão de pull requests no GitHub.\nRedação de documentação e criação de tutoriais para novos contribuidores.',
          location: null,
          startDate: '2021-06-01',
          endDate: '2021-12-31',
        },
      ],
    },
    skills: {
      skills: [
        'JavaScript',
        'TypeScript',
        'React',
        'Node.js',
        'Express',
        'MongoDB',
        'PostgreSQL',
        'Docker',
        'Git',
      ],
      strengths: [],
      preferedTasks: [],
      notPreferedTasks: [],
      languages: [
        { language: 'Português', level: 'Nativo' },
        { language: 'Inglês', level: 'Fluente' },
      ],
    },
    education: {
      schools: [
        {
          school: 'IST',
          name: 'Informática',
          degree: 'Mestrado - Especialista em tecnologias de informação',
          acquiredSkills: ['JavaScript', 'TypeScript', 'React', 'Node.js'],
          location: null,
          degreeStartedAt: '2024-09-01',
          degreeObtainedAt: null,
        },
      ],
      certifications: [],
    },
    isCompleted: true,
    summary:
      'Desenvolvedor Full Stack apaixonado com mais de 4 anos de experiência na criação de aplicações web modernas e de alto desempenho. Domínio completo da stack técnica, desde o frontend com React e TypeScript até o backend com Node.js e PostgreSQL.',
    question: null,
    isReconversion: false,
    complementaryInfo: { hasRQTH: false, mentions: [] },
    firstName: 'Marie',
    lastName: 'Dupont',
    email: '[EMAIL]',
    phone: '[PHONE]',
    hasDriverLicense: true,
    isMobile: true,
    openToTraining: true,
    gender: null,
    hobbiesAndInterests: ['Leitura', 'Viagens', 'Fotografia', 'Caminhadas'],
    location: 'Lisboa',
    mobilityRadius: 200,
    mobilityRange: '200km',
    postalCode: '1000-001',
    pictureBase64: null,
    jobTitle: 'Desenvolvedor Full Stack',
    personalExperiences: [
      {
        title: 'Projeto Open Source',
        description:
          'Contribuição para o desenvolvimento de uma biblioteca JavaScript open source.',
        location: null,
        startDate: '2021-06-01',
        endDate: '2021-12-31',
      },
    ],
    competences: [
      'JavaScript',
      'TypeScript',
      'React',
      'Node.js',
      'Express',
      'MongoDB',
      'PostgreSQL',
      'Docker',
      'Git',
    ],
    languages: [
      { language: 'Português', level: 'Nativo' },
      { language: 'Inglês', level: 'Fluente' },
    ],
    interviewId: 'example-interview-id',
    templateParams: {},
    template: DEFAULT_TEMPLATE_ID,
    locale: { name: 'Português', codeShort: 'pt', codeLong: 'pt-PT' },
  },

  'uk-UA': {
    personalInfo: {
      postalCode: '01001',
      location: 'Київ',
      jobTitle: 'Розробник повного стеку',
      hasDriverLicense: true,
      driverLicenseTypes: 'B',
      openToTraining: true,
      gender: null,
      hobbiesAndInterests: ['Читання', 'Подорожі', 'Фотографія', 'Похід'],
      mobility: { isMobile: true, range: '200km', radius: 200 },
    },
    experiences: {
      professional: [
        {
          company: 'TechSolutions',
          location: 'Київ, Україна',
          title: 'Розробник повного стеку',
          missions:
            'Розробка та створення надійних веб-додатків з використанням React, Node.js та PostgreSQL.\nУчасть у перегляді коду та безперервне вдосконалення процесів розробки.\nНаставництво молодших розробників та проведення сесій обміну знаннями.',
          startDate: '2022-03-01',
          endDate: null,
        },
        {
          company: 'Digital Agency',
          location: 'Львів, Україна',
          title: 'Розробник frontend',
          missions:
            'Створення інтерактивних та адаптивних інтерфейсів користувача з використанням React та TypeScript.\nТісна співпраця з дизайнерами для реалізації макетів Figma.\nОптимізація веб-продуктивності та впровадження E2E-тестів з Cypress.',
          startDate: '2020-06-01',
          endDate: '2022-02-28',
        },
      ],
      personal: [
        {
          title: 'Проект з відкритим вихідним кодом',
          description:
            'Внесок у розробку бібліотеки JavaScript з відкритим вихідним кодом.\nКерування issues та перегляд pull-requests на GitHub.\nНаписання документації та створення підручників для нових учасників.',
          location: null,
          startDate: '2021-06-01',
          endDate: '2021-12-31',
        },
      ],
    },
    skills: {
      skills: [
        'JavaScript',
        'TypeScript',
        'React',
        'Node.js',
        'Express',
        'MongoDB',
        'PostgreSQL',
        'Docker',
        'Git',
      ],
      strengths: [],
      preferedTasks: [],
      notPreferedTasks: [],
      languages: [
        { language: 'Українська', level: 'Рідна' },
        { language: 'Англійська', level: 'Вільно' },
      ],
    },
    education: {
      schools: [
        {
          school: 'КПІ',
          name: 'Інформатика',
          degree: 'Магістр - Експерт з інформаційних технологій',
          acquiredSkills: ['JavaScript', 'TypeScript', 'React', 'Node.js'],
          location: null,
          degreeStartedAt: '2024-09-01',
          degreeObtainedAt: null,
        },
      ],
      certifications: [],
    },
    isCompleted: true,
    summary:
      'Розробник повного стеку з більш ніж 4-річним досвідом створення сучасних високопродуктивних веб-додатків. Володію повним стеком технологій, від фронтенду з React та TypeScript до бекенду з Node.js та PostgreSQL.',
    question: null,
    isReconversion: false,
    complementaryInfo: { hasRQTH: false, mentions: [] },
    firstName: 'Marie',
    lastName: 'Dupont',
    email: '[EMAIL]',
    phone: '[PHONE]',
    hasDriverLicense: true,
    isMobile: true,
    openToTraining: true,
    gender: null,
    hobbiesAndInterests: ['Читання', 'Подорожі', 'Фотографія', 'Похід'],
    location: 'Київ',
    mobilityRadius: 200,
    mobilityRange: '200km',
    postalCode: '01001',
    pictureBase64: null,
    jobTitle: 'Розробник повного стеку',
    personalExperiences: [
      {
        title: 'Проект з відкритим вихідним кодом',
        description: 'Внесок у розробку бібліотеки JavaScript з відкритим вихідним кодом.',
        location: null,
        startDate: '2021-06-01',
        endDate: '2021-12-31',
      },
    ],
    competences: [
      'JavaScript',
      'TypeScript',
      'React',
      'Node.js',
      'Express',
      'MongoDB',
      'PostgreSQL',
      'Docker',
      'Git',
    ],
    languages: [
      { language: 'Українська', level: 'Рідна' },
      { language: 'Англійська', level: 'Вільно' },
    ],
    interviewId: 'example-interview-id',
    templateParams: {},
    template: DEFAULT_TEMPLATE_ID,
    locale: { name: 'Українська', codeShort: 'uk', codeLong: 'uk-UA' },
  },

  'ar-DZ': {
    personalInfo: {
      postalCode: '16000',
      location: 'الجزائر',
      jobTitle: 'مطور ويب متكامل',
      hasDriverLicense: true,
      driverLicenseTypes: 'B',
      openToTraining: true,
      gender: null,
      hobbiesAndInterests: ['القراءة', 'السفر', 'التصوير الفوتوغرافي', 'المشي'],
      mobility: { isMobile: true, range: '200km', radius: 200 },
    },
    experiences: {
      professional: [
        {
          company: 'TechSolutions',
          location: 'الجزائر',
          title: 'مطور ويب متكامل',
          missions:
            'تصميم وتطوير تطبيقات ويب قوية باستخدام React و Node.js و PostgreSQL.\nالمشاركة في مراجعة الكود والتحسين المستمر لعمليات التطوير.\nتوجيه المطورين الشباب وإدارة جلسات مشاركة المعرفة.',
          startDate: '2022-03-01',
          endDate: null,
        },
        {
          company: 'Digital Agency',
          location: 'وهران',
          title: 'مطور واجهة أمامية',
          missions:
            'إنشاء واجهات مستخدم تفاعلية ومتجاوبة باستخدام React و TypeScript.\nالتعاون الوثيق مع المصممين لتنفيذ نماذج Figma.\nتحسين أداء الويب وتنفيذ اختبارات E2E مع Cypress.',
          startDate: '2020-06-01',
          endDate: '2022-02-28',
        },
      ],
      personal: [
        {
          title: 'مشروع مفتوح المصدر',
          description:
            'المساهمة في تطوير مكتبة JavaScript مفتوحة المصدر.\nإدارة المشكلات ومراجعة طلبات السحب على GitHub.\nكتابة الوثائق وإنشاء دروس للمساهمين الجدد.',
          location: null,
          startDate: '2021-06-01',
          endDate: '2021-12-31',
        },
      ],
    },
    skills: {
      skills: [
        'JavaScript',
        'TypeScript',
        'React',
        'Node.js',
        'Express',
        'MongoDB',
        'PostgreSQL',
        'Docker',
        'Git',
      ],
      strengths: [],
      preferedTasks: [],
      notPreferedTasks: [],
      languages: [
        { language: 'العربية', level: 'اللغة الأم' },
        { language: 'الإنجليزية', level: 'طلاقة' },
      ],
    },
    education: {
      schools: [
        {
          school: 'جامعة الجزائر 1',
          name: 'المعلوماتية',
          degree: 'ماجستير - خبير في تقنيات المعلومات',
          acquiredSkills: ['JavaScript', 'TypeScript', 'React', 'Node.js'],
          location: null,
          degreeStartedAt: '2024-09-01',
          degreeObtainedAt: null,
        },
      ],
      certifications: [],
    },
    isCompleted: true,
    summary:
      'مطور ويب متكامل شغوف بأكثر من 4 سنوات من الخبرة في إنشاء تطبيقات ويب حديثة وعالية الأداء. إتقان الكامل ل_stack التقني، من الواجهة الأمامية مع React و TypeScript إلى الخلفية مع Node.js و PostgreSQL.',
    question: null,
    isReconversion: false,
    complementaryInfo: { hasRQTH: false, mentions: [] },
    firstName: 'Marie',
    lastName: 'Dupont',
    email: '[EMAIL]',
    phone: '[PHONE]',
    hasDriverLicense: true,
    isMobile: true,
    openToTraining: true,
    gender: null,
    hobbiesAndInterests: ['القراءة', 'السفر', 'التصوير الفوتوغرافي', 'المشي'],
    location: 'الجزائر',
    mobilityRadius: 200,
    mobilityRange: '200km',
    postalCode: '16000',
    pictureBase64: null,
    jobTitle: 'مطور ويب متكامل',
    personalExperiences: [
      {
        title: 'مشروع مفتوح المصدر',
        description: 'المساهمة في تطوير مكتبة JavaScript مفتوحة المصدر.',
        location: null,
        startDate: '2021-06-01',
        endDate: '2021-12-31',
      },
    ],
    competences: [
      'JavaScript',
      'TypeScript',
      'React',
      'Node.js',
      'Express',
      'MongoDB',
      'PostgreSQL',
      'Docker',
      'Git',
    ],
    languages: [
      { language: 'العربية', level: 'اللغة الأم' },
      { language: 'الإنجليزية', level: 'طلاقة' },
    ],
    interviewId: 'example-interview-id',
    templateParams: {},
    template: DEFAULT_TEMPLATE_ID,
    locale: { name: 'العربية', codeShort: 'ar', codeLong: 'ar-DZ' },
  },

  'ar-MA': {
    personalInfo: {
      postalCode: '10000',
      location: 'الرباط',
      jobTitle: 'مطور ويب متكامل',
      hasDriverLicense: true,
      driverLicenseTypes: 'B',
      openToTraining: true,
      gender: null,
      hobbiesAndInterests: ['القراءة', 'السفر', 'التصوير الفوتوغرافي', 'المشي'],
      mobility: { isMobile: true, range: '200km', radius: 200 },
    },
    experiences: {
      professional: [
        {
          company: 'TechSolutions',
          location: 'الرباط',
          title: 'مطور ويب متكامل',
          missions:
            'تصميم وتطوير تطبيقات ويب قوية باستخدام React و Node.js و PostgreSQL.\nالمشاركة في مراجعة الكود والتحسين المستمر لعمليات التطوير.\nتوجيه المطورين الشباب وإدارة جلسات مشاركة المعرفة.',
          startDate: '2022-03-01',
          endDate: null,
        },
        {
          company: 'Digital Agency',
          location: 'الدار البيضاء',
          title: 'مطور واجهة أمامية',
          missions:
            'إنشاء واجهات مستخدم تفاعلية ومتجاوبة باستخدام React و TypeScript.\nالتعاون الوثيق مع المصممين لتنفيذ نماذج Figma.\nتحسين أداء الويب وتنفيذ اختبارات E2E مع Cypress.',
          startDate: '2020-06-01',
          endDate: '2022-02-28',
        },
      ],
      personal: [
        {
          title: 'مشروع مفتوح المصدر',
          description:
            'المساهمة في تطوير مكتبة JavaScript مفتوحة المصدر.\nإدارة المشكلات ومراجعة طلبات السحب على GitHub.\nكتابة الوثائق وإنشاء دروس للمساهمين الجدد.',
          location: null,
          startDate: '2021-06-01',
          endDate: '2021-12-31',
        },
      ],
    },
    skills: {
      skills: [
        'JavaScript',
        'TypeScript',
        'React',
        'Node.js',
        'Express',
        'MongoDB',
        'PostgreSQL',
        'Docker',
        'Git',
      ],
      strengths: [],
      preferedTasks: [],
      notPreferedTasks: [],
      languages: [
        { language: 'العربية', level: 'اللغة الأم' },
        { language: 'الإنجليزية', level: 'طلاقة' },
      ],
    },
    education: {
      schools: [
        {
          school: 'جامعة محمد الخامس',
          name: 'المعلوماتية',
          degree: 'ماجستير - خبير في تقنيات المعلومات',
          acquiredSkills: ['JavaScript', 'TypeScript', 'React', 'Node.js'],
          location: null,
          degreeStartedAt: '2024-09-01',
          degreeObtainedAt: null,
        },
      ],
      certifications: [],
    },
    isCompleted: true,
    summary:
      'مطور ويب متكامل شغوف بأكثر من 4 سنوات من الخبرة في إنشاء تطبيقات ويب حديثة وعالية الأداء. إتقان الكامل ل_stack التقني، من الواجهة الأمامية مع React و TypeScript إلى الخلفية مع Node.js و PostgreSQL.',
    question: null,
    isReconversion: false,
    complementaryInfo: { hasRQTH: false, mentions: [] },
    firstName: 'Marie',
    lastName: 'Dupont',
    email: '[EMAIL]',
    phone: '[PHONE]',
    hasDriverLicense: true,
    isMobile: true,
    openToTraining: true,
    gender: null,
    hobbiesAndInterests: ['القراءة', 'السفر', 'التصوير الفوتوغرافي', 'المشي'],
    location: 'الرباط',
    mobilityRadius: 200,
    mobilityRange: '200km',
    postalCode: '10000',
    pictureBase64: null,
    jobTitle: 'مطور ويب متكامل',
    personalExperiences: [
      {
        title: 'مشروع مفتوح المصدر',
        description: 'المساهمة في تطوير مكتبة JavaScript مفتوحة المصدر.',
        location: null,
        startDate: '2021-06-01',
        endDate: '2021-12-31',
      },
    ],
    competences: [
      'JavaScript',
      'TypeScript',
      'React',
      'Node.js',
      'Express',
      'MongoDB',
      'PostgreSQL',
      'Docker',
      'Git',
    ],
    languages: [
      { language: 'العربية', level: 'اللغة الأم' },
      { language: 'الإنجليزية', level: 'طلاقة' },
    ],
    interviewId: 'example-interview-id',
    templateParams: {},
    template: DEFAULT_TEMPLATE_ID,
    locale: { name: 'العربية', codeShort: 'ar', codeLong: 'ar-MA' },
  },

  'tr-TR': {
    personalInfo: {
      postalCode: '06000',
      location: 'Ankara',
      jobTitle: 'Tam Yığın Geliştirici',
      hasDriverLicense: true,
      driverLicenseTypes: 'B',
      openToTraining: true,
      gender: null,
      hobbiesAndInterests: ['Okuma', 'Seyahat', 'Fotoğrafçılık', 'Yürüyüş'],
      mobility: { isMobile: true, range: '200km', radius: 200 },
    },
    experiences: {
      professional: [
        {
          company: 'TechSolutions',
          location: 'Ankara, Türkiye',
          title: 'Tam Yığın Geliştirici',
          missions:
            'React, Node.js ve PostgreSQL kullanarak sağlam web uygulamalarının tasarlanması ve geliştirilmesi.\nKod incelemelerine katılma ve geliştirme süreçlerinin sürekli iyileştirilmesi.\nGenç geliştiricilere mentörlük ve bilgi paylaşımı oturumlarının yönetilmesi.',
          startDate: '2022-03-01',
          endDate: null,
        },
        {
          company: 'Digital Agency',
          location: 'İstanbul, Türkiye',
          title: 'Frontend Geliştirici',
          missions:
            'React ve TypeScript ile etkileşimli ve duyarlı kullanıcı arayüzleri oluşturma.\nTasarımcılarla yakın çalışarak Figma tasarımlarını uygulama.\nWeb performansını optimize etme ve Cypress ile E2E testleri uygulama.',
          startDate: '2020-06-01',
          endDate: '2022-02-28',
        },
      ],
      personal: [
        {
          title: 'Açık Kaynak Projesi',
          description:
            'Açık kaynak bir JavaScript kütüphanesinin geliştirilmesine katkıda bulunma.\nGitHub üzerinde issues yönetimi ve pull request incelemesi.\nBelgeler yazma ve yeni katılımcılar için eğitimler oluşturma.',
          location: null,
          startDate: '2021-06-01',
          endDate: '2021-12-31',
        },
      ],
    },
    skills: {
      skills: [
        'JavaScript',
        'TypeScript',
        'React',
        'Node.js',
        'Express',
        'MongoDB',
        'PostgreSQL',
        'Docker',
        'Git',
      ],
      strengths: [],
      preferedTasks: [],
      notPreferedTasks: [],
      languages: [
        { language: 'Türkçe', level: 'Anadili' },
        { language: 'İngilizce', level: 'Akıcı' },
      ],
    },
    education: {
      schools: [
        {
          school: 'ODTÜ',
          name: 'Bilgisayar Bilimleri',
          degree: 'Yüksek Lisans - Bilgi Teknolojileri Uzmanı',
          acquiredSkills: ['JavaScript', 'TypeScript', 'React', 'Node.js'],
          location: null,
          degreeStartedAt: '2024-09-01',
          degreeObtainedAt: null,
        },
      ],
      certifications: [],
    },
    isCompleted: true,
    summary:
      "4 yılı aşkın deneyime sahip, modern ve yüksek performanslı web uygulamaları oluşturmaya tutkulu bir Tam Yığın Geliştirici. React ve TypeScript ile frontend'den Node.js ve PostgreSQL ile backend'e kadar tüm teknoloji yığınına hakimim.",
    question: null,
    isReconversion: false,
    complementaryInfo: { hasRQTH: false, mentions: [] },
    firstName: 'Marie',
    lastName: 'Dupont',
    email: '[EMAIL]',
    phone: '[PHONE]',
    hasDriverLicense: true,
    isMobile: true,
    openToTraining: true,
    gender: null,
    hobbiesAndInterests: ['Okuma', 'Seyahat', 'Fotoğrafçılık', 'Yürüyüş'],
    location: 'Ankara',
    mobilityRadius: 200,
    mobilityRange: '200km',
    postalCode: '06000',
    pictureBase64: null,
    jobTitle: 'Tam Yığın Geliştirici',
    personalExperiences: [
      {
        title: 'Açık Kaynak Projesi',
        description: 'Açık kaynak bir JavaScript kütüphanesinin geliştirilmesine katkıda bulunma.',
        location: null,
        startDate: '2021-06-01',
        endDate: '2021-12-31',
      },
    ],
    competences: [
      'JavaScript',
      'TypeScript',
      'React',
      'Node.js',
      'Express',
      'MongoDB',
      'PostgreSQL',
      'Docker',
      'Git',
    ],
    languages: [
      { language: 'Türkçe', level: 'Anadili' },
      { language: 'İngilizce', level: 'Akıcı' },
    ],
    interviewId: 'example-interview-id',
    templateParams: {},
    template: DEFAULT_TEMPLATE_ID,
    locale: { name: 'Türkçe', codeShort: 'tr', codeLong: 'tr-TR' },
  },

  'ro-RO': {
    personalInfo: {
      postalCode: '010000',
      location: 'București',
      jobTitle: 'Dezvoltator Full Stack',
      hasDriverLicense: true,
      driverLicenseTypes: 'B',
      openToTraining: true,
      gender: null,
      hobbiesAndInterests: ['Citiți', 'Călătorii', 'Fotografie', 'Drumeții'],
      mobility: { isMobile: true, range: '200km', radius: 200 },
    },
    experiences: {
      professional: [
        {
          company: 'TechSolutions',
          location: 'București, România',
          title: 'Dezvoltator Full Stack',
          missions:
            'Concepția și dezvoltarea aplicațiilor web robuste cu React, Node.js și PostgreSQL.\nParticiparea la revizuirea codului și îmbunătățirea continuă a proceselor de dezvoltare.\nMentoratul dezvoltatorilor juniori și conducerea sesiunilor de partajare a cunoștințelor.',
          startDate: '2022-03-01',
          endDate: null,
        },
        {
          company: 'Digital Agency',
          location: 'Cluj-Napoca, România',
          title: 'Dezvoltator Frontend',
          missions:
            'Crearea de interfețe de utilizator interactive și receptive cu React și TypeScript.\nColaborarea strânsă cu designerii pentru a implementa machete Figma.\nOptimizarea performanței web și implementarea testelor E2E cu Cypress.',
          startDate: '2020-06-01',
          endDate: '2022-02-28',
        },
      ],
      personal: [
        {
          title: 'Proiect Open Source',
          description:
            'Contribuție la dezvoltarea unei biblioteci JavaScript open source.\nGestionarea problemelor și revizuirea pull-requests pe GitHub.\nScrierea documentației și crearea de tutoriale pentru noi colaboratori.',
          location: null,
          startDate: '2021-06-01',
          endDate: '2021-12-31',
        },
      ],
    },
    skills: {
      skills: [
        'JavaScript',
        'TypeScript',
        'React',
        'Node.js',
        'Express',
        'MongoDB',
        'PostgreSQL',
        'Docker',
        'Git',
      ],
      strengths: [],
      preferedTasks: [],
      notPreferedTasks: [],
      languages: [
        { language: 'Română', level: 'Limba maternă' },
        { language: 'Engleză', level: 'Fluent' },
      ],
    },
    education: {
      schools: [
        {
          school: 'Politehnica București',
          name: 'Informatică',
          degree: 'Master - Expert în tehnologii informaționale',
          acquiredSkills: ['JavaScript', 'TypeScript', 'React', 'Node.js'],
          location: null,
          degreeStartedAt: '2024-09-01',
          degreeObtainedAt: null,
        },
      ],
      certifications: [],
    },
    isCompleted: true,
    summary:
      'Dezvoltator Full Stack pasionat cu peste 4 ani de experiență în crearea de aplicații web moderne și performante. Stăpânire completă a stack-ului tehnic, de la frontend cu React și TypeScript la backend cu Node.js și PostgreSQL.',
    question: null,
    isReconversion: false,
    complementaryInfo: { hasRQTH: false, mentions: [] },
    firstName: 'Marie',
    lastName: 'Dupont',
    email: '[EMAIL]',
    phone: '[PHONE]',
    hasDriverLicense: true,
    isMobile: true,
    openToTraining: true,
    gender: null,
    hobbiesAndInterests: ['Citiți', 'Călătorii', 'Fotografie', 'Drumeții'],
    location: 'București',
    mobilityRadius: 200,
    mobilityRange: '200km',
    postalCode: '010000',
    pictureBase64: null,
    jobTitle: 'Dezvoltator Full Stack',
    personalExperiences: [
      {
        title: 'Proiect Open Source',
        description: 'Contribuție la dezvoltarea unei biblioteci JavaScript open source.',
        location: null,
        startDate: '2021-06-01',
        endDate: '2021-12-31',
      },
    ],
    competences: [
      'JavaScript',
      'TypeScript',
      'React',
      'Node.js',
      'Express',
      'MongoDB',
      'PostgreSQL',
      'Docker',
      'Git',
    ],
    languages: [
      { language: 'Română', level: 'Limba maternă' },
      { language: 'Engleză', level: 'Fluent' },
    ],
    interviewId: 'example-interview-id',
    templateParams: {},
    template: DEFAULT_TEMPLATE_ID,
    locale: { name: 'Română', codeShort: 'ro', codeLong: 'ro-RO' },
  },
};

export function getCvExemple(locale: string): CvExempleData {
  return CV_DATA[locale as LocaleCode] ?? CV_DATA['fr-FR'];
}

// Backward compat: default export in French
export const CV_EXEMPLE = CV_DATA['fr-FR'];
